import { Box, Portal, Typography } from '@mui/material';
import { useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import briefcaseIcon from '../../assets/briefcase.svg';
import {
  getMobileNavActiveTab,
  isPrimaryMobileNavRoute,
  MOBILE_BOTTOM_NAV_Z,
  MOBILE_FLOATING_ASSIST_OFFSET,
  MOBILE_FLOATING_ASSIST_Z,
  MOBILE_NAV_PORTAL_SELECTOR,
  normalizePathname,
} from '../../config/mobileNavConfig';
import './styles/MobileBottomNav.css';

/* ── Inline SVG icons (from design assets) ── */

/** My trips — briefcase.svg; active state uses same red filter as other nav accents */
const BRIEFCASE_ACTIVE_FILTER =
  'brightness(0) saturate(100%) invert(20%) sepia(85%) saturate(3650%) hue-rotate(325deg) brightness(0.85) contrast(1.05)';

const HomeIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M20.0402 6.82018L14.2802 2.79018C12.7102 1.69018 10.3002 1.75018 8.79023 2.92018L3.78023 6.83018C2.78023 7.61018 1.99023 9.21018 1.99023 10.4702V17.3702C1.99023 19.9202 4.06023 22.0002 6.61023 22.0002H17.3902C19.9402 22.0002 22.0102 19.9302 22.0102 17.3802V10.6002C22.0102 9.25018 21.1402 7.59018 20.0402 6.82018ZM12.7502 18.0002C12.7502 18.4102 12.4102 18.7502 12.0002 18.7502C11.5902 18.7502 11.2502 18.4102 11.2502 18.0002V15.0002C11.2502 14.5902 11.5902 14.2502 12.0002 14.2502C12.4102 14.2502 12.7502 14.5902 12.7502 15.0002V18.0002Z"
      fill={active ? '#CF2048' : '#7E808F'}
    />
  </svg>
);

const MyTripsIcon = ({ active }) => (
  <Box
    component="img"
    src={briefcaseIcon}
    alt=""
    aria-hidden
    sx={{
      width: 24,
      height: 24,
      flexShrink: 0,
      display: 'block',
      filter: active ? BRIEFCASE_ACTIVE_FILTER : 'none',
      transition: 'filter 0.15s ease',
    }}
  />
);

const AssistIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M8.5 19H8C4 19 2 18 2 13V8C2 4 4 2 8 2H16C20 2 22 4 22 8V13C22 17 20 19 16 19H15.5C15.19 19 14.89 19.15 14.7 19.4L13.2 21.4C12.54 22.28 11.46 22.28 10.8 21.4L9.3 19.4C9.14 19.18 8.77 19 8.5 19Z"
      stroke={active ? '#CF2048' : '#7E808F'}
      strokeWidth="1.5"
      strokeMiterlimit="10"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M7 8H17" stroke={active ? '#CF2048' : '#7E808F'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 13H13" stroke={active ? '#CF2048' : '#7E808F'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ProfileIcon = ({ active }) => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12.1202 12.78C12.0502 12.77 11.9602 12.77 11.8802 12.78C10.1202 12.72 8.72021 11.28 8.72021 9.50998C8.72021 7.69998 10.1802 6.22998 12.0002 6.22998C13.8102 6.22998 15.2802 7.69998 15.2802 9.50998C15.2702 11.28 13.8802 12.72 12.1202 12.78Z"
      stroke={active ? '#CF2048' : '#7E808F'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M18.7398 19.3801C16.9598 21.0101 14.5998 22.0001 11.9998 22.0001C9.39977 22.0001 7.03977 21.0101 5.25977 19.3801C5.35977 18.4401 5.95977 17.5201 7.02977 16.8001C9.76977 14.9801 14.2498 14.9801 16.9698 16.8001C18.0398 17.5201 18.6398 18.4401 18.7398 19.3801Z"
      stroke={active ? '#CF2048' : '#7E808F'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
      stroke={active ? '#CF2048' : '#7E808F'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SendIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M5 12H19M19 12L13 6M19 12L13 18" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

/* ── Nav item ── */
const NavItem = ({ onClick, icon, label, active }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '3px',
      py: '8px',
      px: 0,
      cursor: 'pointer',
      WebkitTapHighlightColor: 'transparent',
      userSelect: 'none',
      border: 'none',
      background: 'none',
      font: 'inherit',
      color: 'inherit',
      touchAction: 'manipulation',
    }}
  >
    {icon}
    <Typography
      sx={{
        fontSize: { xs: '9px', sm: '10px' },
        fontWeight: active ? 700 : 400,
        color: active ? '#CF2048' : '#7E808F',
        fontFamily: 'Host Grotesk, sans-serif',
        lineHeight: 1.2,
        letterSpacing: '0.02em',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        maxWidth: '100%',
      }}
    >
      {label}
    </Typography>
  </Box>
);

/* ── Component ── */
const MobileBottomNav = ({
  onAssistClick,
  onProfileClick,
  onDismissAssist,
  assistActive = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const normalizedPath = normalizePathname(location.pathname);
  const [inputText, setInputText] = useState('');
  const portalRootRef = useRef(null);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') return;
    const root = portalRootRef.current;
    // Production prerender writes the portaled bar and tabs into the HTML.
    // React then mounts a second set. The baked copy never hears assistActive,
    // so its input stays on top of the tabs after Assist opens.
    document.querySelectorAll(MOBILE_NAV_PORTAL_SELECTOR).forEach((node) => {
      if (root && (node === root || root.contains(node))) return;
      node.remove();
    });
  });

  if (!isPrimaryMobileNavRoute(normalizedPath)) return null;

  const activeTab = getMobileNavActiveTab(normalizedPath, { assistActive });
  const showFloatingAssistBar =
    normalizedPath === '/' && !assistActive;

  const handleSend = () => {
    const msg = inputText.trim();
    if (onAssistClick) {
      onAssistClick(msg || undefined);
      setInputText('');
      return;
    }
    if (msg && window.openChatbot) {
      window.openChatbot(msg);
    } else if (window.openChatbot) {
      window.openChatbot();
    }
    setInputText('');
  };

  const handleAssistNavClick = () => {
    if (onAssistClick) {
      onAssistClick();
      return;
    }
    window.openChatbot && window.openChatbot();
  };

  const handleHomeClick = () => {
    if (assistActive && onDismissAssist) {
      onDismissAssist();
    }
    if (normalizedPath !== '/') {
      navigate('/');
    }
  };

  const nav = (
    <>
      {showFloatingAssistBar && (
        <Box
          data-yovo-mobile-assist-bar
          sx={{
            display: { xs: 'flex', sm: 'flex', md: 'none' },
            position: 'fixed',
            bottom: MOBILE_FLOATING_ASSIST_OFFSET,
            left: 0,
            right: 0,
            zIndex: MOBILE_FLOATING_ASSIST_Z,
            px: '12px',
            pb: '8px',
            pointerEvents: 'none',
          }}
        >
          <Box className="yovo-mobile-assist-bar-inner" sx={{ pointerEvents: 'auto' }}>
              <input
                type="text"
                placeholder="Ask Yovo Assist travel itineraries..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleSend();
                }}
                autoComplete="off"
              />
              <Box onClick={handleSend} className="yovo-mobile-assist-send" role="button" aria-label="Send">
                <SendIcon />
              </Box>
          </Box>
        </Box>
      )}

      <Box
        data-yovo-mobile-bottom-nav
        sx={{
          display: { xs: 'flex', sm: 'flex', md: 'none' },
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: MOBILE_BOTTOM_NAV_Z,
          background: '#FFFFFF',
          borderTop: '1px solid #E8E9ED',
          paddingBottom: 'env(safe-area-inset-bottom)',
          height: MOBILE_FLOATING_ASSIST_OFFSET,
          alignItems: 'center',
          boxShadow: '0 -2px 12px rgba(0,0,0,0.06)',
        }}
      >
        <NavItem
          onClick={handleHomeClick}
          icon={<HomeIcon active={activeTab === 'home'} />}
          label="Home"
          active={activeTab === 'home'}
        />
        <NavItem
          onClick={handleAssistNavClick}
          icon={<AssistIcon active={activeTab === 'assist'} />}
          label="Yovo Assist"
          active={activeTab === 'assist'}
        />
        <NavItem
          onClick={() => navigate('/my-trips')}
          icon={<MyTripsIcon active={activeTab === 'my-trips'} />}
          label="My trips"
          active={activeTab === 'my-trips'}
        />
        <NavItem
          onClick={() => {
            if (onProfileClick) {
              onProfileClick();
              return;
            }
            navigate('/account');
          }}
          icon={<ProfileIcon active={activeTab === 'profile'} />}
          label="Profile"
          active={activeTab === 'profile'}
        />
      </Box>
    </>
  );

  /* Portal to body so iOS Safari keeps position:fixed on the viewport (not a scroll ancestor). */
  return (
    <Portal container={typeof document !== 'undefined' ? document.body : undefined}>
      <Box ref={portalRootRef} data-yovo-mobile-nav-live="" sx={{ display: 'contents' }}>
        {nav}
      </Box>
    </Portal>
  );
};

export default MobileBottomNav;
